import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import MemberMembershipService from "../../../../services/member_membership_service";
import { useTenant } from "../../../../hooks/useTenant";
import ConfirmDialog from "../../../../components/Common/ConfirmDialog";

/**
 * useMemberMemberships
 * Hook para gerenciar os planos adquiridos de um membro no perfil.
 * Busca memberMemberships e permite cancelar.
 */
const useMemberMemberships = (member) => {
    const { idTenant, user } = useTenant();
    const [memberships, setMemberships] = useState([]);
    const [loading, setLoading] = useState(false);
    const [canceling, setCanceling] = useState(null);

    const fetchMemberships = useCallback(async () => {
        if (!idTenant || !member?.id) return;

        setLoading(true);
        try {
            const data = await MemberMembershipService.getByMember(idTenant, member.id);

            // Ordenar: ativos primeiro, depois por data de criação (mais recente)
            const sorted = data.sort((a, b) => {
                if (a.status === "active" && b.status !== "active") return -1;
                if (a.status !== "active" && b.status === "active") return 1;
                const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
                const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
                return dateB - dateA;
            });

            setMemberships(sorted);
        } catch (error) {
            console.error("Erro ao buscar planos do membro:", error);
            toast.error("Erro ao carregar planos do aluno.");
        } finally {
            setLoading(false);
        }
    }, [idTenant, member?.id]);

    useEffect(() => {
        fetchMemberships();
    }, [fetchMemberships]);

    const handleCancelMembership = async (membership) => {
        const isConfirmed = await ConfirmDialog({
            title: "Cancelar plano?",
            text: `Tem certeza que deseja cancelar o plano "${membership.membershipName}" de "${member.fullName}"? Esta ação não pode ser desfeita.`,
            confirmButtonText: "Sim, cancelar plano!",
            confirmButtonColor: "#ff3d60",
        });

        if (!isConfirmed) return;

        setCanceling(membership.id);
        try {
            await MemberMembershipService.cancel(membership.id, idTenant, user, membership, "Cancelado pelo gestor");
            toast.success("Plano cancelado com sucesso.");
            await fetchMemberships();
        } catch (error) {
            console.error("Erro ao cancelar plano:", error);
            toast.error(error.message || "Erro ao cancelar plano.");
        } finally {
            setCanceling(null);
        }
    };

    return {
        memberships,
        loading,
        canceling,
        handleCancelMembership,
        refresh: fetchMemberships
    };
};

export default useMemberMemberships;
